import axios from 'axios';
import { getApprovals } from '@/api/approvals';
import { baseUrl, HttpMethods } from '../../base';
import type {
  BudgetChangeOrderDetailsResponse,
  BudgetChangeOrderDetailsVariables,
  BudgetChangeOrderListResponse,
  BudgetChangeOrderListVariables,
  ChangeOrderApprovalsResponse,
  ChangeOrderRefType,
  ChangeOrderSummaryResponse,
  ChangeOrderSummaryVariables,
  CreateBudgetChangeOrderVariables,
  CreateDeliverableChangeOrderVariables,
  CreateMilestoneChangeOrderVariables,
  DeliverableChangeOrderDetailsResponse,
  DeliverableChangeOrderDetailsVariables,
  DeliverableChangeOrderListResponse,
  DeliverableChangeOrderListVariables,
  LineItemListResponse,
  LineItemListVariables,
  MilestoneChangeOrderDetailsResponse,
  MilestoneChangeOrderDetailsVariables,
  MilestoneChangeOrderListResponse,
  MilestoneChangeOrderListVariables,
  PatchBudgetChangeOrderVariables,
  PatchDeliverableChangeOrderVariables,
  PatchMilestoneChangeOrderVariables,
} from './types';

export async function getChangeOrderSummary({ projectId }: ChangeOrderSummaryVariables) {
  const res = await axios.request<ChangeOrderSummaryResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/summary`,
    params: {
      refType: 'project',
      refId: projectId,
    },
  });

  return res.data;
}

/** MILESTONES */

export async function createMilestoneChangeOrder(payload: CreateMilestoneChangeOrderVariables) {
  const res = await axios.request<{ id: number }>({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/projects/change-orders/milestones`,
    data: payload,
  });

  return res.data;
}

export async function patchMilestoneChangeOrder({ changeOrderId, ...payload }: PatchMilestoneChangeOrderVariables) {
  const res = await axios.request<ChangeOrderSummaryResponse>({
    method: HttpMethods.PATCH,
    url: `${baseUrl.SHERPA}/projects/change-orders/${changeOrderId}/milestones`,
    data: payload,
  });

  return res.data;
}

export async function getMilestoneChangeOrderList(params: MilestoneChangeOrderListVariables) {
  const res = await axios.request<MilestoneChangeOrderListResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/milestones`,
    params,
  });

  return res.data;
}

export async function getMilestoneChangeOrderDetails({ id }: MilestoneChangeOrderDetailsVariables) {
  const res = await axios.request<MilestoneChangeOrderDetailsResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/${id}/milestones`,
  });

  return res.data;
}

/** DELIVERABLES */

export async function createDeliverableChangeOrder(payload: CreateDeliverableChangeOrderVariables) {
  const res = await axios.request<{ id: number }>({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/projects/change-orders/deliverables`,
    data: payload,
  });

  return res.data;
}

export async function patchDeliverableChangeOrder({ changeOrderId, ...payload }: PatchDeliverableChangeOrderVariables) {
  const res = await axios.request<ChangeOrderSummaryResponse>({
    method: HttpMethods.PATCH,
    url: `${baseUrl.SHERPA}/projects/change-orders/${changeOrderId}/deliverables`,
    data: payload,
  });

  return res.data;
}

export async function getDeliverableChangeOrderList(params: DeliverableChangeOrderListVariables) {
  const res = await axios.request<DeliverableChangeOrderListResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/deliverables`,
    params,
  });

  return res.data;
}

export async function getDeliverableChangeOrderDetails({ id }: DeliverableChangeOrderDetailsVariables) {
  const res = await axios.request<DeliverableChangeOrderDetailsResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/${id}/deliverables`,
  });

  return res.data;
}

/** BUDGET LINE ITEMS */

export async function createBudgetChangeOrder(payload: CreateBudgetChangeOrderVariables) {
  const res = await axios.request<{ id: number }>({
    method: HttpMethods.POST,
    url: `${baseUrl.SHERPA}/projects/change-orders/budget-line-items`,
    data: payload,
  });

  return res.data;
}

export async function patchBudgetChangeOrder({ changeOrderId, ...payload }: PatchBudgetChangeOrderVariables) {
  const res = await axios.request<ChangeOrderSummaryResponse>({
    method: HttpMethods.PATCH,
    url: `${baseUrl.SHERPA}/projects/change-orders/${changeOrderId}/budget-line-items`,
    data: payload,
  });

  return res.data;
}

export async function getBudgetChangeOrderList(params: BudgetChangeOrderListVariables) {
  const res = await axios.request<BudgetChangeOrderListResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/budget-line-items`,
    params,
  });

  return res.data;
}

export async function getBudgetChangeOrderDetails({ id }: BudgetChangeOrderDetailsVariables) {
  const res = await axios.request<BudgetChangeOrderDetailsResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/${id}/budget-line-items`,
  });

  return res.data;
}

export async function getLineItemOptions(params: LineItemListVariables) {
  const res = await axios.request<LineItemListResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/budget-line-items/dropdown`,
    params,
  });

  return res.data;
}

export async function getBudgetLineItemSources(params: LineItemListVariables) {
  const res = await axios.request<LineItemListResponse>({
    method: HttpMethods.GET,
    url: `${baseUrl.SHERPA}/projects/change-orders/budget-line-items/sources/dropdown`,
    params,
  });

  return res.data;
}

/** COMMON APIS */

export async function getChangeOrderApprovals(params: { refType: ChangeOrderRefType; refId: number }) {
  return getApprovals(params) as unknown as Promise<ChangeOrderApprovalsResponse>;
}

export async function deleteChangeOrderDeliverable({ changeOrderId }: Pick<PatchDeliverableChangeOrderVariables, 'changeOrderId'>) {
  const res = await axios.request({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/projects/change-orders/${changeOrderId}/deliverables`,
  });

  return res.data;
}

export async function deleteChangeOrderMilestones({ changeOrderId }: Pick<PatchDeliverableChangeOrderVariables, 'changeOrderId'>) {
  const res = await axios.request({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/projects/change-orders/${changeOrderId}/milestones`,
  });

  return res.data;
}

export async function deleteChangeOrderBudgetLineItem({ changeOrderId }: Pick<PatchDeliverableChangeOrderVariables, 'changeOrderId'>) {
  const res = await axios.request({
    method: HttpMethods.DELETE,
    url: `${baseUrl.SHERPA}/projects/change-orders/${changeOrderId}/budget-line-items`,
  });

  return res.data;
}

export * from './types';
